import React, { useState, useEffect } from 'react';
import { Button, Input, Card, Tag, Spin } from 'antd';
import BRAND from '../config/brand';
import { 
  SearchOutlined, 
  FireOutlined,
  DownloadOutlined,
  StarOutlined,
  ArrowRightOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { skillsApi } from '../services/api';
import styled from 'styled-components';

const HeroSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 60px 20px 40px;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
`;

const HeroTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 12px;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 24px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
`;

const ContentSection = styled.div`
  padding: 32px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
`;

const SkillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const SkillCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const SkillHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const SkillIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${props => props.color || '#667eea'} 0%, ${props => props.color2 || '#764ba2'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: white;
`;

const SkillInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 2px;
  }
`;

const SkillDesc = styled.p`
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SkillStats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #999;

  .stat {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const categoryColors = {
  creation: ['#667eea', '#764ba2'],
  distribution: ['#f093fb', '#f5576c'],
  marketing: ['#4facfe', '#00f2fe'],
  data: ['#43e97b', '#38f9d7'],
  general: ['#fa709a', '#fee140'],
  other: ['#30cfd0', '#330867'],
};

function Home() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const response = await skillsApi.getSkills({ limit: 8 });
      if (response.data.success) {
        setSkills(response.data.data || []);
      }
    } catch (error) {
      console.error('加载技能失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      navigate(`/skills?search=${encodeURIComponent(searchValue)}`);
    }
  };

  const formatNumber = (num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + '万';
    }
    return num.toString();
  };

  const getCategoryColor = (category) => {
    return categoryColors[category] || ['#667eea', '#764ba2'];
  };

  const renderSkillCard = (skill) => (
    <SkillCard 
      key={skill.id} 
      onClick={() => navigate(`/skill/${skill.id}`)}
    >
      <SkillHeader>
        <SkillIcon 
          color={getCategoryColor(skill.category)[0]}
          color2={getCategoryColor(skill.category)[1]}
        >
          {skill.displayName?.charAt(0) || 'S'}
        </SkillIcon>
        <SkillInfo>
          <div className="name">
            {skill.displayName}
            {skill.type === 1 && <Tag color="blue" size="small">官方</Tag>}
          </div>
        </SkillInfo>
      </SkillHeader>
      <SkillDesc>{skill.description}</SkillDesc>
      <SkillStats>
        <span className="stat">
          <StarOutlined style={{ color: '#faad14' }} /> {skill.rating}
        </span>
        <span className="stat">
          <DownloadOutlined /> {formatNumber(skill.downloadCount)}
        </span>
      </SkillStats>
    </SkillCard>
  );

  return (
    <div>
      <HeroSection>
        <HeroTitle>{BRAND.heroHeadline}</HeroTitle>
        <HeroSubtitle>{BRAND.heroSubheadline}</HeroSubtitle>
        
        <Input.Search
          placeholder="搜索技能..."
          size="large"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={handleSearch}
          enterButton={<><SearchOutlined /> 搜索</>}
          style={{ maxWidth: 500, margin: '0 auto', display: 'block' }}
        />

        <ActionButtons>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/publish')}
          >
            发布技能
          </Button>
          <Button 
            size="large"
            onClick={() => navigate('/skills')}
          >
            浏览全部
          </Button>
        </ActionButtons>
      </HeroSection>

      <ContentSection>
        <SectionHeader>
          <h2><FireOutlined /> 热门技能</h2>
          <Button type="link" onClick={() => navigate('/skills')}>
            查看更多 <ArrowRightOutlined />
          </Button>
        </SectionHeader>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <SkillGrid>
            {skills.map(renderSkillCard)}
          </SkillGrid>
        )}
      </ContentSection>
    </div>
  );
}

export default Home;
